import type { Tier } from 'constants/payloads/realtime';

export interface DelayedFlowState {
  /** The client classifies the viewer as free or signed out. */
  shouldHaveFree: boolean;
  /** Screenshot mode renders injected rows and never explains a live feed. */
  hasInjectedPageData: boolean;
  /** Realtime rows drive the table (the socket is live, or reconnecting with rows held). */
  usingRealtime: boolean;
  /** The tier the server granted in its hello; null before the first hello. */
  realtimeTier: Tier | null;
  realtimeRowCount: number;
}

/**
 * True when a free viewer's delayed realtime feed is connected and holds no trades yet.
 *
 * A free viewer's tape runs 30 minutes late. When today's session starts the realtime feed is
 * empty, and it stays empty until the first trade clears the delay. The tables use this, with the
 * feed's session date, to explain the wait instead of looking broken. It counts only once the server confirms the free tier, so a
 * paid viewer who is briefly classified free on the client is never asked to subscribe.
 *
 * The legacy polling fallback is deliberately not covered: its `free_option_flow` view serves the
 * newest date with a trade at least 30 minutes old, so before 10:00 AM ET it returns the previous
 * session rather than an empty table.
 */
export const isAwaitingDelayedFlow = (state: DelayedFlowState): boolean =>
  state.shouldHaveFree &&
  !state.hasInjectedPageData &&
  state.usingRealtime &&
  state.realtimeTier === 'free' &&
  state.realtimeRowCount === 0;
